import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Container, Row, Col, Image } from 'react-bootstrap';
import domainName from '../../domainName';
import queryString from 'query-string';
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import loadingGif from '../../img/loading.gif';

const Notification = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { UserId } = useSelector((state) => state.login);
    const [t] = useTranslation("global");

    const [userId, setUserId] = useState('');
    const [notiList, setNotiList] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(true);
    const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
    const sentinelRef = useRef(null);

    const [isIOS, setIsIOS] = useState(false);
    const [isAndroid, setIsAndroid] = useState(false);
    const [isWebView, setIsWebView] = useState(false);

    const fetchNotifications = async (page = 1) => {
        if (page === 1) setLoading(true);
        try {
            const response = await domainName.serverAPICall(
                domainName.debugAPI + `/user/listNotificationsActivity`,
                "POST",
                JSON.stringify({
                    UserId: userId,
                    Filter_Type: 'ALL',
                    PageNum: page,
                    ResultNum: 20
                })
            );
            const data = await domainName.handleServerAPICallErrors(response);
            const newData = data.Data || [];

            if (page === 1) {
                setNotiList(newData);
            } else {
                setNotiList(prev => [...prev, ...newData]);
            }

            if (newData.length < 20) {
                setHasMore(false);
            }
        } catch (error) {
            alert(t("error.An error occurred. Please try again later."));
        } finally {
            if (page === 1) setLoading(false);
            setIsFetchingNextPage(false);
        }
    };

    const loadMore = () => {
        if (hasMore && !loading && !isFetchingNextPage) {
            setIsFetchingNextPage(true);
            const nextPage = currentPage + 1;
            setCurrentPage(nextPage);
            fetchNotifications(nextPage);
        }
    };

    const unreadNotification = async (notificationId) => {
        if (userId) {
            await domainName
                .serverAPICall(
                    domainName.debugAPI + `/user/updateNotificationsActivityToSeenById`,
                    "POST",
                    JSON.stringify({
                        "UserId": userId,
                        "NotificationId": notificationId,
                    })
                )
                .then(domainName.handleServerAPICallErrors)
                .then(() => {
                    // console.log('Notification marked as read!');
                })
                .catch((error) => {
                    console.log(error);
                });
        }
    };

    // 🔁 useEffect 1 — setup userId, device info
    useEffect(() => {
        const userAgent = navigator.userAgent;
    
        const isIOSDevice = !!userAgent.match(/iPad|iPhone|iPod/);
        const isAndroidDevice = !!userAgent.match(/Android/);
    
        const isWebViewDevice = (() => {
            if (isIOSDevice && (window.navigator.standalone || window.webkit)) {
                return true;
            }
            if (isAndroidDevice && (window.chrome === undefined)) {
                return true;
            }
            return false;
        })();
    
        setIsIOS(isIOSDevice);
        setIsAndroid(isAndroidDevice);
        setIsWebView(isWebViewDevice);
    
        const queryParams = queryString.parse(location.search);
        const resolvedUserId = queryParams.userId || UserId;
        if (resolvedUserId) {
            setUserId(resolvedUserId);
        }
    }, []);

    // 🔁 useEffect 2 — fetch first page when userId ready
    useEffect(() => {
        if (userId) {
            setCurrentPage(1);
            setHasMore(true);
            fetchNotifications(1);
        }
    }, [userId]);

    // 🔁 useEffect 3 — setup scroll observer
    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting) loadMore();
            },
            { threshold: 1 }
        );

        if (sentinelRef.current) observer.observe(sentinelRef.current);
        return () => observer.disconnect();
    }, [notiList, loading, hasMore]);

    return (
        <div style={{ minHeight: "200px" }}>
            <Container>
                <Row>
                    <Col className='col-12'>
                        {loading ? (
                            <div style={{ textAlign: 'center', paddingTop: '2em' }}>
                                <img src={loadingGif} alt="Loading" style={{ width: '40px' }} />
                            </div>
                        ) : (
                            notiList.map((item, index) => (
                                <div key={index} style={{ cursor: 'pointer' }} onClick={async () => {
                                    await unreadNotification(item._id);
                                    const link = item.Link.toString().split("advisor2uapp://")[1];
                                    if ((isIOS || isAndroid) && isWebView) {
                                        window.postMessage(`${item.Link}`);
                                    } else {
                                        navigate(`/${link}`);
                                    }
                                }}>
                                    <Row>
                                        <Col className='d-flex p-3' style={{ backgroundColor: item.Seen ? 'transparent' : '#F8F8F8', borderBottom: '1px solid #D9D9D9' }}>
                                            <Image src={item.ImageDisplay} className='round-profile-image' />
                                            <div style={{ marginLeft: '15px' }}>
                                                <div className={`smaller-font ${item.Seen ? '' : 'fw-bold'}`}>{item.Message}</div>
                                                <div className='smaller-font'>{item.DisplayDateTime}</div>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            ))
                        )}
                    </Col>
                </Row>
            </Container>
            <div ref={sentinelRef} style={{ height: '1px' }}></div>
        </div>
    );
};

export default Notification;
